// ── AR Flow — NOVA INTERFACE: painel da meta no Dashboard ─────────────────────
// Junta num bloco só o que o consultor precisa ver do mês: quanto vendeu,
// quanto falta, meta por dia, super meta, "Você poderia estar com" e a frase
// motivadora. Só visual: recebe os números já calculados pelo Dashboard e
// usa os mesmos cliques (detalhes de vendido / recebido / a receber).

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";
const brl = v => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const FRASES = [
  "\"Tudo que a mente humana pode acreditar, ela pode conceber.\" Bora pra cima! 🚀",
  "\"O sucesso é a soma de pequenos esforços repetidos dia após dia.\" Você é capaz! 💪",
  "\"Acredite em si mesmo e chegará um dia em que os outros não terão outra escolha senão acreditar com você.\" 🌟",
  "\"A diferença entre o possível e o impossível está na determinação.\" Vai buscar! 🔥",
  "\"Grandes conquistas exigem grandes atitudes. Você tem tudo isso!\" ✨",
  "\"Cada cliente é uma oportunidade. Cada sim é uma vitória. Continue!\" 🏆",
];

function Barra({ valor, max, cor, fundo }) {
  const p = max > 0 ? Math.min(100, Math.round((valor / max) * 100)) : 0;
  return (
    <div role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} style={{ height: 10, background: fundo, borderRadius: 99, overflow: "hidden" }}>
      <div style={{ width: `${p}%`, height: "100%", background: cor, borderRadius: 99, transition: "width 0.6s ease" }} />
    </div>
  );
}

export default function DashboardMeta({
  theme, titulo, periodo, meta, superMeta, vendido, vendas, ticketMedio, falta, metaDia, diasRestantes, mesAtual,
  emOrcamentos, recebido, aReceber, motivacional, mostrarPotencial,
  onVendido, onRecebido, onAReceber,
}) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const pct = meta > 0 ? Math.min(100, Math.round((vendido / meta) * 100)) : 0;
  const pctSuper = superMeta > 0 ? Math.min(100, Math.round((vendido / superMeta) * 100)) : 0;
  const potencial = vendido + emOrcamentos;
  const frase = FRASES[new Date().getDate() % FRASES.length];
  const clicavel = { cursor: "pointer", background: "none", border: "none", padding: 0, textAlign: "left", fontFamily: "inherit", color: "inherit" };

  return (
    <section aria-label="Meta do mês" style={{ background: "#fff", border: `1px solid ${line}`, borderRadius: 4, marginBottom: 20 }}>
      <div style={{ padding: "18px 20px", borderBottom: `1px solid ${line}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 700, color: th.text }}>{titulo}</div>
          <div style={{ fontSize: 13, color: th.muted }}>{periodo}</div>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", margin: "12px 0 8px" }}>
          <button type="button" onClick={onVendido} style={clicavel}>
            <span style={{ fontFamily: MONO, fontSize: 34, fontWeight: 500, color: th.text }}>{brl(vendido)}</span>
          </button>
          <span style={{ fontSize: 15, color: th.muted }}>de {brl(meta)} · {pct}%</span>
        </div>
        <Barra valor={vendido} max={meta} cor={pct >= 100 ? "#1E6B4A" : th.accent} fundo={th.sandDark || "#F4EBDD"} />
        <div style={{ fontSize: 14, fontWeight: 600, color: th.text, marginTop: 10 }}>{motivacional}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", borderBottom: `1px solid ${line}` }}>
        {[
          { rotulo: "Falta para a meta", valor: falta > 0 ? brl(falta) : "Meta batida! 🏆", cor: falta > 0 ? th.text : "#1E6B4A" },
          { rotulo: mesAtual ? `Meta por dia · ${diasRestantes} dia${diasRestantes !== 1 ? "s" : ""}` : "Meta por dia", valor: metaDia > 0 ? brl(metaDia) : "—", cor: metaDia > 0 ? "#B45309" : th.muted },
          { rotulo: `${vendas} venda${vendas !== 1 ? "s" : ""} fechada${vendas !== 1 ? "s" : ""}`, valor: ticketMedio > 0 ? brl(ticketMedio) : "—", sub: "ticket médio", cor: th.text },
        ].map((c, i) => (
          <div key={i} style={{ padding: "14px 20px", borderLeft: i ? `1px solid ${line}` : "none" }}>
            <div style={{ fontSize: 12, color: th.muted }}>{c.rotulo}</div>
            <div style={{ fontFamily: MONO, fontSize: 20, color: c.cor, marginTop: 2 }}>{c.valor}</div>
            {c.sub && <div style={{ fontSize: 11, color: th.muted }}>{c.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ padding: "14px 20px", borderBottom: `1px solid ${line}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
          <span style={{ fontWeight: 700, color: th.text }}>🚀 Super meta · {brl(superMeta)}</span>
          <span style={{ fontFamily: MONO, color: th.muted }}>{pctSuper}%</span>
        </div>
        <Barra valor={vendido} max={superMeta} cor={th.accentLight || th.accent} fundo={th.sandDark || "#F4EBDD"} />
      </div>

      {mostrarPotencial && emOrcamentos > 0 && (
        <div style={{ padding: "16px 20px", background: th.sand || "#FAF8F3", borderBottom: `1px solid ${line}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: th.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>💡 Você poderia estar com</div>
          <div style={{ fontFamily: MONO, fontSize: 28, color: th.accent, margin: "4px 0" }}>{brl(potencial)}</div>
          <div style={{ fontSize: 13, color: th.muted }}>{brl(vendido)} já vendidos + {brl(emOrcamentos)} em orçamentos abertos</div>
          <div style={{ fontSize: 14, fontStyle: "italic", color: th.text, marginTop: 10 }}>{frase}</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", padding: "12px 20px", fontSize: 13, color: th.muted }}>
        <button type="button" onClick={onRecebido} style={clicavel}>💵 Recebido: <strong style={{ fontFamily: MONO, color: th.text }}>{brl(recebido)}</strong></button>
        <button type="button" onClick={onAReceber} style={clicavel}>⏳ A receber: <strong style={{ fontFamily: MONO, color: aReceber > 0 ? "#B45309" : th.text }}>{brl(aReceber)}</strong></button>
        <span>Vendido = valor cheio da venda, independente do pagamento.</span>
      </div>
    </section>
  );
}